import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { InfiniteScroll } from "@/components/ui/infinite-scroll";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Briefcase,
  Plus,
  Search,
  Filter,
  Kanban,
  List,
  CheckCircle,
  XCircle,
  ArrowRight,
  DollarSign,
  IndianRupee,
  Calendar,
  Building,
  User,
  Trash2,
  Edit,
  TrendingUp,
  AlertTriangle,
  Eye,
  ChevronLeft,
  ChevronRight,
  Move,
  GripVertical,
} from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { DealRecord, PipelineRecord, PipelineStageRecord, MasterLookupRecord } from "@/types";
import { useAuth } from "@/context/AuthContext";
import { StageMoveDialog, type StageMoveRequest } from "@/components/deals/StageMoveDialog";
import { FilterBar } from "@/components/filters/FilterBar";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, type OwnerFacet } from "@/lib/filters";
import {
  CalendarClock as FilterCloseIcon,
  CalendarDays as FilterDateIcon,
  Layers3 as FilterStageIcon,
  UserCircle2 as FilterOwnerIcon,
} from "lucide-react";

// Remembered per browser; "all" / "any" mean no filter
const DEAL_FILTER_DEFAULTS = { owner: "all", stage: "all", close: "any", created: "any" };

/** Close-date filter -> API params (overdue / no date are special values) */
const closeParams = (close: string): Record<string, string> =>
  close === "overdue" ? { close: "overdue" } : close === "no_date" ? { close: "none" } : dateRangeParams(close, "close");

export function DealsPage() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState<DealRecord[]>([]);
  const [pipeline, setPipeline] = useState<PipelineRecord | null>(null);
  const [pipelineList, setPipelineList] = useState<PipelineRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"kanban" | "list">("kanban");
  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const [listPageSize, setListPageSize] = useState(30);
  const [stageMeta, setStageMeta] = useState<Record<string, { page: number; total: number; pageSize: number; loading: boolean }>>({});
  const [accountLabel, setAccountLabel] = useState("");
  // An existing contact the new deal is for (when started from a contact or account page)
  const [presetContact, setPresetContact] = useState<{ id: string; name: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  useEffect(() => setSearch(searchParams.get("search") || ""), [searchParams]);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const { user } = useAuth();
  const dealFilters = usePersistentFilters("deals", DEAL_FILTER_DEFAULTS);
  const [dealOptions, setDealOptions] = useState<{ total: number; owners: OwnerFacet[] }>({ total: 0, owners: [] });
  const filtersReady = React.useRef(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [moveDeal, setMoveDeal] = useState<DealRecord | null>(null);
  // Every stage change goes through the gate check (and its dialog when something is missing)
  const [moveRequest, setMoveRequest] = useState<StageMoveRequest>(null);
  const [wonGate, setWonGate] = useState<{ allowed: boolean; missing: string[]; can_override: boolean } | null>(null);
  const [wonReason, setWonReason] = useState("");
  const [wonDeal, setWonDeal] = useState<DealRecord | null>(null);
  const [lostDeal, setLostDeal] = useState<DealRecord | null>(null);
  const [deleteDeal, setDeleteDeal] = useState<DealRecord | null>(null);

  // Kanban Drag & Drop and Scroll Controls
  const kanbanRef = useRef<HTMLDivElement>(null);
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [dropSlot, setDropSlot] = useState<{ stageId: string; index: number } | null>(null);

  const autoScrollSpeedRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);

  const scrollKanban = (direction: "left" | "right") => {
    if (kanbanRef.current) {
      const scrollAmount = direction === "left" ? -300 : 300;
      kanbanRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Continuous animation frame loop for butter-smooth edge scrolling
  useEffect(() => {
    let animId: number;
    const loop = () => {
      if (isDraggingRef.current && autoScrollSpeedRef.current !== 0 && kanbanRef.current) {
        kanbanRef.current.scrollBy({ left: autoScrollSpeedRef.current, behavior: "auto" });
      }
      animId = requestAnimationFrame(loop);
    };
    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  }, []);

  // Track global dragover to detect when dragged card approaches Kanban container edges
  useEffect(() => {
    const handleGlobalDragOver = (e: DragEvent) => {
      if (!isDraggingRef.current || !kanbanRef.current) return;
      e.preventDefault();
      const rect = kanbanRef.current.getBoundingClientRect();

      // Only trigger auto-scroll if cursor is in or near the vertical bounds of the Kanban board
      if (e.clientY < rect.top - 60 || e.clientY > rect.bottom + 60) {
        autoScrollSpeedRef.current = 0;
        return;
      }

      const EDGE_ZONE = 150; // Detection zone width in px from container boundary
      const MAX_SPEED = 24;  // Max px per animation frame
      const MIN_SPEED = 5;   // Min starting speed

      if (e.clientX >= rect.right - EDGE_ZONE && e.clientX <= rect.right + 60) {
        // Approaching right edge -> Slide Right
        const distanceIntoZone = Math.max(0, e.clientX - (rect.right - EDGE_ZONE));
        const ratio = Math.min(1, distanceIntoZone / EDGE_ZONE);
        autoScrollSpeedRef.current = MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ratio;
      } else if (e.clientX <= rect.left + EDGE_ZONE && e.clientX >= rect.left - 60) {
        // Approaching left edge -> Slide Left
        const distanceIntoZone = Math.max(0, (rect.left + EDGE_ZONE) - e.clientX);
        const ratio = Math.min(1, distanceIntoZone / EDGE_ZONE);
        autoScrollSpeedRef.current = -(MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ratio);
      } else {
        // In the neutral middle zone
        autoScrollSpeedRef.current = 0;
      }
    };

    const handleDragEnd = () => {
      isDraggingRef.current = false;
      autoScrollSpeedRef.current = 0;
      setDropSlot(null);
      setDraggedDealId(null);
      setDragOverStageId(null);
    };

    window.addEventListener("dragover", handleGlobalDragOver);
    window.addEventListener("dragend", handleDragEnd);
    window.addEventListener("drop", handleDragEnd);
    return () => {
      window.removeEventListener("dragover", handleGlobalDragOver);
      window.removeEventListener("dragend", handleDragEnd);
      window.removeEventListener("drop", handleDragEnd);
    };
  }, []);

  const handleDropAtSlot = async (
    dealId: string,
    targetStage: PipelineStageRecord,
    targetIndex: number
  ) => {
    isDraggingRef.current = false;
    autoScrollSpeedRef.current = 0;
    setDropSlot(null);
    setDraggedDealId(null);
    setDragOverStageId(null);

    const sourceDeal = deals.find((d) => d.id === dealId);
    if (!sourceDeal) return;

    if (sourceDeal.stage_id !== targetStage.id) {
      if (targetStage.is_won_stage) {
        openWon(sourceDeal);
        return;
      }
      if (targetStage.is_lost_stage) {
        openLost(sourceDeal);
        return;
      }
      setMoveRequest({ dealId: sourceDeal.id, dealName: sourceDeal.name, stage: { id: targetStage.id, name: targetStage.name } });
      return;
    }

    const otherDeals = deals.filter((d) => d.id !== dealId);
    const stageDeals = otherDeals.filter((d) => d.stage_id === targetStage.id);
    const nonTargetDeals = otherDeals.filter((d) => d.stage_id !== targetStage.id);

    const safeIdx = Math.max(0, Math.min(stageDeals.length, targetIndex));
    const updatedSource = {
      ...sourceDeal,
      stage_id: targetStage.id,
      stage: targetStage,
      probability: targetStage.probability_percent,
      weighted_amount: Number(
        ((Number(sourceDeal.amount || 0) * targetStage.probability_percent) / 100).toFixed(2)
      ),
    };
    stageDeals.splice(safeIdx, 0, updatedSource);

    // Instant state update with new priority order
    setDeals([...nonTargetDeals, ...stageDeals]);

    try {
      const orderedIds = stageDeals.map((d) => d.id);
      await api.patch("/api/Deal/Reorder", { deal_ids: orderedIds });
    } catch (err: any) {
      console.error("Reorder error:", err);
    }
  };

  // Forms
  const [accountMode, setAccountMode] = useState<"existing" | "new">("existing");
  const [form, setForm] = useState({
    name: "",
    account_id: "",
    company_name: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    stage_id: "",
    amount: 1000,
    expected_close_date: "",
  });

  const [moveStageId, setMoveStageId] = useState("");
  const [wonForm, setWonForm] = useState({ won_amount: 0, actual_close_date: "" });
  const [lostForm, setLostForm] = useState({ lost_reason: "", lost_notes: "" });

  const [lostReasons, setLostReasons] = useState<MasterLookupRecord[]>([]);

  const { success, error } = useToast();

  // Filtering happens in the API (role scope first, then these filters)
  const dealQuery = (extra: { stage_id?: string; page?: number; page_size?: number } = {}) => {
    const f = dealFilters.values;
    return toQuery({
      status: statusFilter === "ALL" ? undefined : statusFilter,
      search: search.trim(),
      owner: f.owner,
      stage_id: extra.stage_id !== undefined ? extra.stage_id : f.stage,
      ...closeParams(f.close),
      ...dateRangeParams(f.created, "created"),
      page: extra.page,
      page_size: extra.page_size ?? 30,
    });
  };

  const fillList = async (page: number) => {
    const paged = unwrapPage<DealRecord>(await api.get(`/api/Deal/List${dealQuery({ page })}`));
    setDeals(paged.rows);
    setListTotal(paged.total);
    setListPageSize(paged.page_size);
    setListPage(paged.page);
  };

  const fillKanban = async (stages: { id: string }[]) => {
    const stageFilter = dealFilters.values.stage;
    const columns = stageFilter && stageFilter !== "all" ? stages.filter((s) => s.id === stageFilter) : stages;
    const results = await Promise.all(
      columns.map(async (stg) => {
        const paged = unwrapPage<DealRecord>(await api.get(`/api/Deal/List${dealQuery({ stage_id: stg.id, page: 1 })}`));
        return [stg.id, paged] as const;
      }),
    );
    setDeals(results.flatMap(([, paged]) => paged.rows));
    setStageMeta(
      Object.fromEntries(
        results.map(([id, paged]) => [id, { page: paged.page, total: paged.total, pageSize: paged.page_size, loading: false }]),
      ),
    );
  };

  const loadMoreStage = async (stageId: string) => {
    const meta = stageMeta[stageId];
    if (!meta || meta.loading || meta.page * meta.pageSize >= meta.total) return;
    setStageMeta((current) => ({ ...current, [stageId]: { ...meta, loading: true } }));
    try {
      const paged = unwrapPage<DealRecord>(await api.get(`/api/Deal/List${dealQuery({ stage_id: stageId, page: meta.page + 1 })}`));
      setDeals((prev) => {
        const ids = new Set(prev.map((d) => d.id));
        return [...prev, ...paged.rows.filter((row) => !ids.has(row.id))];
      });
      setStageMeta((current) => ({
        ...current,
        [stageId]: { page: paged.page, total: paged.total, pageSize: paged.page_size, loading: false },
      }));
    } catch (err: any) {
      setStageMeta((current) => ({ ...current, [stageId]: { ...meta, loading: false } }));
      error("Fetch Error", err.message || "Failed to load more deals");
    }
  };

  const loadDealOptions = () =>
    api
      .get("/api/Deal/FilterOptions")
      .then((res) => setDealOptions(unwrapObject(res)))
      .catch(() => undefined);

  /** Reloads only the deals (used when a filter changes) */
  const loadDeals = async (page = viewMode === "list" ? listPage : 1) => {
    setLoading(true);
    try {
      if (viewMode === "list") {
        await fillList(page);
      } else if (pipeline?.stages) {
        await fillKanban(pipeline.stages);
      }
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load deals");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPipeline = async (pipeId: string) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/Pipeline/ById/${pipeId}`);
      const pipeData = unwrapObject<PipelineRecord>(res);
      setPipeline(pipeData);
      const stages = pipeData?.stages || [];
      if (viewMode === "list") await fillList(1);
      else await fillKanban(stages);
    } catch (err: any) {
      error("Error", err.message || "Could not switch pipeline");
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [pipeRes, lostRes, listRes] = await Promise.all([
        api.get("/api/Pipeline/Default"),
        api.get("/api/MasterLookup/ByCategory/DEAL_LOST_REASON"),
        api.get("/api/Pipeline/List").catch(() => null),
      ]);
      loadDealOptions();

      const pipeData = unwrapObject<PipelineRecord>(pipeRes);
      setPipeline(pipeData);
      if (listRes) {
        setPipelineList(unwrapArray<PipelineRecord>(listRes));
      }
      const stages = pipeData?.stages || [];
      if (viewMode === "list") await fillList(1);
      else await fillKanban(stages);
      setLostReasons(unwrapArray<MasterLookupRecord>(lostRes));
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load deals or pipeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Reload deals when a filter, the status or the search changes (search waits until typing pauses)
  useEffect(() => {
    if (!filtersReady.current) {
      filtersReady.current = true;
      return;
    }
    const t = setTimeout(() => {
      if (viewMode === "list" && listPage !== 1) setListPage(1);
      else loadDeals(1);
    }, search.trim() ? 350 : 0);
    return () => clearTimeout(t);
  }, [dealFilters.values, statusFilter, search, viewMode]);

  useEffect(() => {
    if (!filtersReady.current || viewMode !== "list") return;
    const t = setTimeout(() => loadDeals(listPage), 0);
    return () => clearTimeout(t);
  }, [listPage]);

  const openAdd = (preset?: { accountId?: string; accountName?: string; contactId?: string; contactName?: string }) => {
    if (!pipeline?.stages?.length) {
      error("Error", "No pipeline stages found");
      return;
    }
    const defaultStage = pipeline.stages.find((s) => !s.is_won_stage && !s.is_lost_stage) || pipeline.stages[0];
    setAccountMode("existing");
    setAccountLabel(preset?.accountName || "");
    setPresetContact(preset?.contactId ? { id: preset.contactId, name: preset.contactName || "Selected contact" } : null);
    setForm({
      name: "",
      account_id: preset?.accountId || "",
      company_name: "",
      contact_name: "",
      contact_phone: "",
      contact_email: "",
      stage_id: defaultStage?.id || "",
      amount: 1000,
      expected_close_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    });
    setAddOpen(true);
  };

  // "New deal" from an account or contact page: /deals?create=true&account_id=…&contact_id=…
  useEffect(() => {
    if (searchParams.get("create") !== "true" || !pipeline?.stages?.length) return;
    openAdd({
      accountId: searchParams.get("account_id") || undefined,
      accountName: searchParams.get("account_name") || undefined,
      contactId: searchParams.get("contact_id") || undefined,
      contactName: searchParams.get("contact_name") || undefined,
    });
    setSearchParams({}, { replace: true });
  }, [searchParams, pipeline]);

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pipeline) return;
    if (accountMode === "new" && !form.company_name.trim()) {
      error("Validation", "Company / Account name is required");
      return;
    }
    if (accountMode === "existing" && !form.account_id) {
      error("Validation", "Please select a customer account or enter a new company name");
      return;
    }
    setActionLoading(true);
    try {
      await api.post("/api/Deal/Insert", {
        name: form.name.trim(),
        account_id: accountMode === "existing" ? form.account_id : undefined,
        company_name: accountMode === "new" ? form.company_name.trim() : undefined,
        primary_contact_id: presetContact && accountMode === "existing" ? presetContact.id : undefined,
        contact_name: presetContact ? undefined : form.contact_name?.trim() || undefined,
        contact_phone: presetContact ? undefined : form.contact_phone?.trim() || undefined,
        contact_email: presetContact ? undefined : form.contact_email?.trim() || undefined,
        pipeline_id: pipeline.id,
        stage_id: form.stage_id,
        amount: Number(form.amount),
        expected_close_date: form.expected_close_date ? new Date(form.expected_close_date) : undefined,
      });
      success("Opportunity Added", "Successfully created and placed on the Sales Pipeline");
      setAddOpen(false);
      loadData();
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not create deal");
    } finally {
      setActionLoading(false);
    }
  };

  const openMove = (d: DealRecord) => {
    setMoveDeal(d);
    setMoveStageId(d.stage_id);
  };

  const handleMoveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!moveDeal) return;
    const target = stages.find((s) => s.id === moveStageId);
    if (!target || target.id === moveDeal.stage_id) {
      setMoveDeal(null);
      return;
    }
    if (target.is_won_stage) {
      const d = moveDeal;
      setMoveDeal(null);
      openWon(d);
      return;
    }
    if (target.is_lost_stage) {
      const d = moveDeal;
      setMoveDeal(null);
      openLost(d);
      return;
    }
    setMoveRequest({ dealId: moveDeal.id, dealName: moveDeal.name, stage: { id: target.id, name: target.name } });
    setMoveDeal(null);
  };

  const openWon = (d: DealRecord) => {
    setWonDeal(d);
    setWonGate(null);
    setWonReason("");
    setWonForm({
      won_amount: Number(d.amount),
      actual_close_date: new Date().toISOString().slice(0, 10),
    });
    const wonStage = stages.find((s) => s.is_won_stage);
    if (wonStage) {
      api
        .get(`/api/Deal/Gates/${d.id}?target=${wonStage.id}`)
        .then((res: any) => setWonGate({ allowed: !!res?.move?.allowed, missing: res?.move?.missing || [], can_override: !!res?.can_override }))
        .catch(() => undefined);
    }
  };

  const handleWonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wonDeal) return;
    setActionLoading(true);
    try {
      await api.patch(`/api/Deal/MarkWon/${wonDeal.id}`, {
        won_amount: Number(wonForm.won_amount),
        actual_close_date: new Date(wonForm.actual_close_date),
        ...(wonGate && !wonGate.allowed ? { override: true, reason: wonReason.trim() } : {}),
      });
      success("Deal Closed as Won", "Deal successfully closed as Won.");
      setWonDeal(null);
      loadData();
    } catch (err: any) {
      error("Failed", err.message || "Could not close deal");
    } finally {
      setActionLoading(false);
    }
  };

  const openLost = (d: DealRecord) => {
    setLostDeal(d);
    setLostForm({ lost_reason: "", lost_notes: "" });
  };

  const handleLostSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lostDeal) return;
    if (!lostForm.lost_reason.trim()) {
      error("Validation", "Lost reason is mandatory");
      return;
    }
    setActionLoading(true);
    try {
      await api.patch(`/api/Deal/MarkLost/${lostDeal.id}`, {
        lost_reason: lostForm.lost_reason.trim(),
        lost_notes: lostForm.lost_notes?.trim(),
      });
      success("Deal Closed as Lost", "Stage and history updated");
      setLostDeal(null);
      loadData();
    } catch (err: any) {
      error("Failed", err.message || "Could not update deal");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!deleteDeal) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/Deal/Delete/${deleteDeal.id}`);
      success("Deal Deleted", "Deal removed from pipeline");
      setDeleteDeal(null);
      loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not delete deal");
    } finally {
      setActionLoading(false);
    }
  };

  // The API already applied every filter
  const filteredDeals = deals;
  const visibleTotal =
    viewMode === "list" ? listTotal : Object.values(stageMeta).reduce((sum, meta) => sum + meta.total, 0);

  // Calculate Pipeline Metrics
  const totalValue = filteredDeals.reduce((sum, d) => sum + Number(d.amount || 0), 0);
  const weightedValue = filteredDeals.reduce((sum, d) => sum + Number(d.weighted_amount || 0), 0);
  const wonCount = filteredDeals.filter((d) => d.deal_status === "WON").length;
  const openCount = filteredDeals.filter((d) => d.deal_status === "OPEN").length;

  const stages = pipeline?.stages || [];

  return (
    <div className="space-y-3">
      {/* HubSpot Style Header: Title + Views Tabs + Pipeline Switcher + Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-1 border-b border-border/40">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold tracking-tight text-foreground">
            Deals
          </h1>

          {/* HubSpot View Tabs */}
          <div className="flex items-center bg-muted/60 p-0.5 rounded-lg border border-border/50">
            <button
              type="button"
              onClick={() => dealFilters.set("owner", "all")}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                dealFilters.values.owner === "all"
                  ? "bg-background text-foreground shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              All deals
            </button>
            <button
              type="button"
              onClick={() => {
                if (user?.Id) dealFilters.set("owner", String(user.Id));
              }}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                user?.Id && dealFilters.values.owner === String(user.Id)
                  ? "bg-background text-foreground shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              My deals
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Pipeline Selector */}
          {pipelineList.length > 1 ? (
            <select
              value={pipeline?.id || ""}
              onChange={(e) => handleSelectPipeline(e.target.value)}
              className="h-8 px-2.5 text-xs font-semibold bg-background border border-border/80 rounded-lg shadow-2xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {pipelineList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          ) : (
            <div className="h-8 px-3 text-xs font-semibold bg-muted/50 border border-border/60 rounded-lg flex items-center text-foreground">
              {pipeline?.name || "Sales Pipeline"}
            </div>
          )}

          {/* View Toggle */}
          <div className="flex items-center bg-muted/60 rounded-lg p-0.5 border border-border/50">
            <button
              type="button"
              onClick={() => setViewMode("kanban")}
              title="Board view"
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === "kanban"
                  ? "bg-background shadow-2xs text-foreground font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Kanban className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              title="Table view"
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === "list"
                  ? "bg-background shadow-2xs text-foreground font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <List className="size-3.5" />
            </button>
          </div>

          <Button
            onClick={() => openAdd()}
            className="h-8 px-3 text-xs font-semibold gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-2xs"
          >
            <Plus className="size-3.5" />
            New Opportunity
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search deals, accounts..." }}
        fields={[
          { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(dealOptions.owners, dealOptions.total, user?.Id), defaultValue: "all" },
          {
            name: "stage",
            label: "Stage",
            icon: FilterStageIcon,
            type: "select",
            options: [{ value: "all", label: "All stages" }, ...(pipeline?.stages || []).map((st) => ({ value: st.id, label: st.name }))],
            defaultValue: "all",
          },
          { name: "close", label: "Expected close", icon: FilterCloseIcon, type: "date", dates: "future", defaultValue: "any" },
          { name: "created", label: "Created", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={dealFilters.values}
        onChange={(name, value) => dealFilters.set(name as keyof typeof DEAL_FILTER_DEFAULTS, value)}
        onReset={() => {
          dealFilters.reset();
          setStatusFilter("ALL");
        }}
        shown={visibleTotal || filteredDeals.length}
        total={dealOptions.total || undefined}
        noun="deals"
      >
        <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
          <Filter className="size-3" /> Status:
        </span>
        <div className="flex gap-1">
          {["ALL", "OPEN", "WON", "LOST"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 text-xs rounded-md font-medium border transition-colors ${
                statusFilter === s
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background hover:bg-muted text-muted-foreground"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </FilterBar>

      {/* Main View Area */}
      {loading ? (
        <TableSkeleton />
      ) : viewMode === "kanban" ? (
        /* Kanban Board View */
        <div className="space-y-2">
          {/* Subtle scroll buttons & count */}
          <div className="flex items-center justify-between px-1 text-xs text-muted-foreground">
            <span className="text-[11px] font-medium text-muted-foreground">
              {stages.length} stages • {visibleTotal || filteredDeals.length} deals
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => scrollKanban("left")}
                className="h-6.5 px-2 text-[11px] gap-1 rounded-md border shadow-2xs hover:bg-muted font-medium"
                title="Scroll Left"
              >
                <ChevronLeft className="size-3" /> Left
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => scrollKanban("right")}
                className="h-6.5 px-2 text-[11px] gap-1 rounded-md border shadow-2xs hover:bg-muted font-medium"
                title="Scroll Right"
              >
                Right <ChevronRight className="size-3" />
              </Button>
            </div>
          </div>

          {/* Scrollable Columns Container */}
          <div
            ref={kanbanRef}
            className="flex gap-3 overflow-x-auto pb-2 pt-0.5 items-start h-[calc(100vh-215px)] min-h-[500px]"
          >
            {stages.map((stg) => {
              const stageDeals = filteredDeals.filter((d) => d.stage_id === stg.id);
              const stageSum = stageDeals.reduce((sum, d) => sum + Number(d.amount || 0), 0);
              const isOver = dragOverStageId === stg.id;
              const isCurrentStageDrop = dropSlot?.stageId === stg.id;
              const currentDropIndex = isCurrentStageDrop ? dropSlot.index : -1;

              return (
                <div
                  key={stg.id}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dragOverStageId !== stg.id) setDragOverStageId(stg.id);
                    if (draggedDealId && (!dropSlot || dropSlot.stageId !== stg.id)) {
                      setDropSlot({ stageId: stg.id, index: stageDeals.length });
                    }
                  }}
                  onDragLeave={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                    setDragOverStageId(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOverStageId(null);
                    const sourceId = e.dataTransfer.getData("text/plain") || draggedDealId;
                    const targetIdx = dropSlot?.stageId === stg.id ? dropSlot.index : stageDeals.length;
                    setDropSlot(null);
                    setDraggedDealId(null);
                    if (sourceId) {
                      handleDropAtSlot(sourceId, stg, targetIdx);
                    }
                  }}
                  className={`w-[275px] min-w-[275px] max-w-[275px] shrink-0 rounded-xl bg-slate-100/70 dark:bg-muted/30 border border-slate-200/80 dark:border-border/60 p-2.5 flex flex-col h-full ${
                    isOver && !dropSlot
                      ? "border-primary ring-2 ring-primary/40 bg-primary/5 shadow-2xs"
                      : ""
                  }`}
                >
                  {/* Stage Header */}
                  <div className="pb-2 mb-2 border-b border-slate-200/70 dark:border-border/60">
                    <div className="flex items-center justify-between gap-1.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="size-2 rounded-full shrink-0"
                          style={{ backgroundColor: stg.color_code || "#3b82f6" }}
                        />
                        <span className="font-semibold text-xs text-foreground truncate" title={stg.name}>
                          {stg.name}
                        </span>
                      </div>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4.5 font-bold">
                        {stageMeta[stg.id]?.total ?? stageDeals.length}
                      </Badge>
                    </div>

                    <div className="text-[11px] text-muted-foreground mt-1 flex items-center justify-between">
                      <span className="font-mono font-bold text-foreground">
                        {formatINR(stageSum)}
                      </span>
                      <span className="text-[10px] text-muted-foreground/80">
                        {stg.probability_percent}% prob
                      </span>
                    </div>
                  </div>

                  {/* Cards Container */}
                  <div
                    className="space-y-2 overflow-y-auto pr-0.5 flex-1 min-h-0 flex flex-col"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      if (draggedDealId && (!dropSlot || dropSlot.stageId !== stg.id)) {
                        setDropSlot({ stageId: stg.id, index: stageDeals.length });
                      }
                    }}
                  >
                    {stageDeals.length === 0 ? (
                      <div
                        className={`flex-1 min-h-[140px] flex items-center justify-center p-4 text-center border-2 border-dashed rounded-lg text-xs transition-colors ${
                          isOver || (isCurrentStageDrop && draggedDealId)
                            ? "border-primary bg-primary/10 text-primary font-semibold"
                            : "text-muted-foreground/60 border-muted/80"
                        }`}
                      >
                        {isOver || (isCurrentStageDrop && draggedDealId) ? "Drop deal here" : "No deals in stage"}
                      </div>
                    ) : (
                      stageDeals.map((deal, idx) => {
                        const isWon = deal.deal_status === "WON";
                        const isLost = deal.deal_status === "LOST";
                        const isDragging = draggedDealId === deal.id;
                        const isSpaceBefore =
                          isCurrentStageDrop && currentDropIndex === idx && draggedDealId !== deal.id;

                        return (
                          <div
                            key={deal.id}
                            className="relative transition-all duration-150"
                            style={{
                              paddingTop: isSpaceBefore ? "90px" : "0px",
                            }}
                          >
                            {/* Visual Drop Slot opening above this card */}
                            {isSpaceBefore && (
                              <div className="absolute top-0 left-0 right-0 h-20 rounded-lg border-2 border-dashed border-primary bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary select-none pointer-events-none shadow-xs">
                                <div className="flex items-center gap-1.5">
                                  <Move className="size-3.5" />
                                  <span>Drop deal here</span>
                                </div>
                              </div>
                            )}

                            <div
                              draggable={true}
                              onDragStart={(e) => {
                                e.dataTransfer.setData("text/plain", deal.id);
                                e.dataTransfer.effectAllowed = "move";
                                isDraggingRef.current = true;
                                setDraggedDealId(deal.id);
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.dataTransfer.dropEffect = "move";
                                if (!draggedDealId) return;
                                const rect = e.currentTarget.getBoundingClientRect();
                                const midY = rect.top + rect.height / 2;
                                const targetIdx = e.clientY < midY ? idx : idx + 1;
                                if (!dropSlot || dropSlot.stageId !== stg.id || dropSlot.index !== targetIdx) {
                                  setDropSlot({ stageId: stg.id, index: targetIdx });
                                }
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const sourceId = e.dataTransfer.getData("text/plain") || draggedDealId;
                                const targetIdx = dropSlot?.stageId === stg.id ? dropSlot.index : idx;
                                if (sourceId) {
                                  handleDropAtSlot(sourceId, stg, targetIdx);
                                }
                              }}
                              onDragEnd={() => {
                                isDraggingRef.current = false;
                                autoScrollSpeedRef.current = 0;
                                setDropSlot(null);
                                setDraggedDealId(null);
                                setDragOverStageId(null);
                              }}
                              onClick={() => {
                                if (isDraggingRef.current) return;
                                navigate(`/deals/${deal.id}`);
                              }}
                              className={`p-3 rounded-lg bg-card border border-border/80 shadow-xs hover:shadow-md space-y-2 cursor-grab active:cursor-grabbing group select-none hover:border-primary/40 transition-all ${
                                isDragging ? "opacity-30 border-dashed border-primary" : ""
                              }`}
                            >
                              {/* Card Header: Drag Handle + Deal Name + Delete action */}
                              <div className="flex items-start gap-1 justify-between">
                                <div className="flex items-start gap-1 flex-1 min-w-0">
                                  <div
                                    title="Drag card"
                                    className="mt-0.5 -ml-1 p-0.5 rounded text-muted-foreground/40 group-hover:text-muted-foreground cursor-grab active:cursor-grabbing shrink-0 transition-colors"
                                  >
                                    <GripVertical className="size-3.5" />
                                  </div>
                                  <h4 className="font-semibold text-xs text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                                    {deal.name}
                                  </h4>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteDeal(deal);
                                  }}
                                  title="Delete Deal"
                                  className="size-5 shrink-0 opacity-0 group-hover:opacity-100 inline-flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all -mr-1"
                                >
                                  <Trash2 className="size-3" />
                                </button>
                              </div>

                              {/* Details: Account & Contact */}
                              <div className="space-y-1 text-xs text-muted-foreground">
                                <div className="flex items-center gap-1.5 font-medium text-foreground/90 truncate text-[11px]">
                                  <Building className="size-3 shrink-0 text-muted-foreground/70" />
                                  <span className="truncate">
                                    {deal.account?.name || "Independent Account"}
                                  </span>
                                </div>

                                {deal.primary_contact ? (
                                  <div className="flex items-center gap-1.5 truncate text-[10px] text-muted-foreground/80">
                                    <User className="size-3 shrink-0 text-muted-foreground/70" />
                                    <span className="truncate">
                                      {`${deal.primary_contact.first_name} ${deal.primary_contact.last_name || ""}`.trim()}
                                    </span>
                                  </div>
                                ) : null}
                              </div>

                              {/* Card Footer: Amount, Date, Badges, Owner Circle */}
                              <div className="flex items-center justify-between pt-1.5 border-t border-border/50 text-xs">
                                <div className="font-mono font-bold text-foreground text-xs">
                                  {formatINR(deal.amount)}
                                </div>
                                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                  {deal.expected_close_date && !isNaN(new Date(deal.expected_close_date).getTime()) && (
                                    <span className="flex items-center gap-0.5">
                                      <Calendar className="size-2.5" />
                                      {new Date(deal.expected_close_date).toLocaleDateString(undefined, {
                                        month: "short",
                                        day: "numeric",
                                      })}
                                    </span>
                                  )}

                                  {isWon ? (
                                    <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[9px] h-4 px-1 font-semibold">
                                      Won
                                    </Badge>
                                  ) : isLost ? (
                                    <Badge variant="destructive" className="text-[9px] h-4 px-1 font-semibold">
                                      Lost
                                    </Badge>
                                  ) : deal.gate_progress ? (
                                    <span
                                      className={`text-[9px] px-1 py-0.2 rounded font-semibold ${
                                        deal.gate_progress.met === deal.gate_progress.total
                                          ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                          : "bg-muted text-muted-foreground"
                                      }`}
                                      title="Gate requirements"
                                    >
                                      {deal.gate_progress.met === deal.gate_progress.total
                                        ? "✓"
                                        : `${deal.gate_progress.met}/${deal.gate_progress.total}`}
                                    </span>
                                  ) : null}

                                  {deal.owner?.first_name ? (
                                    <div
                                      title={`Owner: ${deal.owner.first_name} ${deal.owner.last_name || ""}`}
                                      className="size-4.5 rounded-full bg-muted border border-border text-[9px] font-bold text-foreground flex items-center justify-center shrink-0 uppercase"
                                    >
                                      {deal.owner.first_name[0]}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}

                    {/* If dropSlot is after all cards */}
                    {isCurrentStageDrop &&
                      currentDropIndex >= stageDeals.length &&
                      draggedDealId && (
                        <div className="h-20 shrink-0 rounded-lg border-2 border-dashed border-primary bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary select-none pointer-events-none shadow-xs">
                          <div className="flex items-center gap-1.5">
                            <Move className="size-3.5" />
                            <span>Drop deal here</span>
                          </div>
                        </div>
                      )}
                    <InfiniteScroll
                      hasMore={(() => {
                        const meta = stageMeta[stg.id];
                        return !!meta && meta.page * meta.pageSize < meta.total;
                      })()}
                      loading={!!stageMeta[stg.id]?.loading}
                      onLoad={() => loadMoreStage(stg.id)}
                    />
                  </div>

                  {/* Stage Total Footer */}
                  <div className="mt-2 pt-1.5 border-t border-slate-200/70 dark:border-border/60 text-[11px] text-muted-foreground flex items-center justify-between px-0.5">
                    <span className="font-mono font-bold text-foreground">
                      {formatINR(stageSum)}
                    </span>
                    <span className="text-[10px] text-muted-foreground">Total amount</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Table List View */
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 text-xs">
                <TableHead>Deal Name</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Probability</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expected Close</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDeals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-xs text-muted-foreground">
                    No deals match criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredDeals.map((d) => (
                  <TableRow key={d.id} className="text-xs">
                    <TableCell
                      onClick={() => navigate(`/deals/${d.id}`)}
                      className="font-semibold text-foreground hover:text-primary cursor-pointer"
                    >
                      {d.name}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{d.account?.name || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal text-xs" style={{ borderColor: d.stage?.color_code }}>
                        {d.stage?.name || "Unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono font-semibold">{formatINR(d.amount)}</TableCell>
                    <TableCell className="font-mono">{d.probability}%</TableCell>
                    <TableCell>
                      <Badge
                        variant={d.deal_status === "WON" ? "default" : d.deal_status === "LOST" ? "destructive" : "secondary"}
                        className={d.deal_status === "WON" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : ""}
                      >
                        {d.deal_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {d.expected_close_date ? new Date(d.expected_close_date).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="View Details"
                        onClick={() => navigate(`/deals/${d.id}`)}
                        className="size-8 text-primary hover:bg-primary/10"
                      >
                        <Eye className="size-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Change Stage" onClick={() => openMove(d)} className="size-8">
                        <ArrowRight className="size-3.5 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Delete" onClick={() => setDeleteDeal(d)} className="size-8 text-destructive">
                        <Trash2 className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <ListPager page={listPage} pageSize={listPageSize} total={listTotal} onPage={setListPage} />
        </div>
      )}

      {/* Add Deal / Opportunity Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-2xl md:max-w-3xl">
          <form onSubmit={handleAddSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Briefcase className="size-5 text-primary" />
                Add Opportunity to Pipeline
              </DialogTitle>
              <DialogDescription>
                Add a new prospect or project opportunity directly into your active sales pipeline.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 py-3 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="deal_name">Opportunity / Deal Name *</Label>
                <Input
                  id="deal_name"
                  placeholder="e.g. 5,000 Metres Fabric Supply or CCTV Installation"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              {/* Company / Account Selection (Toggle Existing vs New) */}
              <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs sm:text-sm font-semibold">Client Company / Account *</Label>
                  <div className="flex rounded-md bg-muted p-0.5 border">
                    <button
                      type="button"
                      onClick={() => setAccountMode("existing")}
                      className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${
                        accountMode === "existing"
                          ? "bg-background text-foreground shadow-xs font-semibold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Existing
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccountMode("new")}
                      className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${
                        accountMode === "new"
                          ? "bg-background text-foreground shadow-xs font-semibold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      + New Company
                    </button>
                  </div>
                </div>

                {accountMode === "existing" ? (
                  <SearchSelect
                    endpoint="/api/Account/List"
                    value={form.account_id}
                    selectedLabel={accountLabel}
                    getLabel={(row) => row.name}
                    placeholder="Search accounts"
                    allowEmpty
                    emptyLabel="Select an existing account"
                    onChange={(id, row) => {
                      // A contact chosen for another account no longer fits
                      if (id !== form.account_id) setPresetContact(null);
                      setForm((p) => ({ ...p, account_id: id }));
                      setAccountLabel(String(row?.name || ""));
                    }}
                  />
                ) : (
                  <Input
                    id="company_name"
                    placeholder="Enter company or client organization name (e.g. Acme Tech)"
                    value={form.company_name}
                    onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))}
                    required={accountMode === "new"}
                  />
                )}
              </div>

              {/* Primary Contact Details */}
              {presetContact && accountMode === "existing" ? (
                <div className="rounded-lg border bg-muted/30 p-3 flex items-center justify-between gap-3 text-sm">
                  <span>
                    <span className="text-xs font-semibold text-muted-foreground block">Primary contact</span>
                    {presetContact.name}
                  </span>
                  <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setPresetContact(null)}>
                    Use someone else
                  </Button>
                </div>
              ) : (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                <Label className="text-xs sm:text-sm font-semibold">Primary Contact Details (Optional)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
                  <Input
                    placeholder="Contact Name"
                    value={form.contact_name}
                    onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))}
                  />
                  <Input
                    placeholder="Phone / Mobile"
                    value={form.contact_phone}
                    onChange={(e) => setForm((p) => ({ ...p, contact_phone: e.target.value }))}
                  />
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={form.contact_email}
                    onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))}
                  />
                </div>
              </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="amount">Expected Value (₹) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm((p) => ({ ...p, amount: Number(e.target.value) }))}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="stage_id">Pipeline Stage *</Label>
                  <select
                    id="stage_id"
                    className="w-full h-9 sm:h-10 rounded-md border border-input bg-background px-3 sm:px-3.5 text-xs sm:text-sm focus:ring-1 focus:ring-ring"
                    value={form.stage_id}
                    onChange={(e) => setForm((p) => ({ ...p, stage_id: e.target.value }))}
                  >
                    {stages.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.probability_percent}%)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="expected_close_date">Expected Close Date</Label>
                <Input
                  id="expected_close_date"
                  type="date"
                  value={form.expected_close_date}
                  onChange={(e) => setForm((p) => ({ ...p, expected_close_date: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter className="mt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? "Saving..." : "Add to Pipeline"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Move Stage Dialog */}
      <Dialog open={!!moveDeal} onOpenChange={(open) => !open && setMoveDeal(null)}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleMoveSubmit}>
            <DialogHeader>
              <DialogTitle>Advance Pipeline Stage</DialogTitle>
              <DialogDescription>
                Move <strong>{moveDeal?.name}</strong> to a different pipeline stage.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="move_stage_select">Select Target Stage</Label>
                <select
                  id="move_stage_select"
                  className="w-full h-9 sm:h-10 rounded-md border border-input bg-background px-3 sm:px-3.5 text-xs sm:text-sm focus:ring-1 focus:ring-ring"
                  value={moveStageId}
                  onChange={(e) => setMoveStageId(e.target.value)}
                >
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.probability_percent}%)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMoveDeal(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? "Moving..." : "Confirm Move"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <StageMoveDialog request={moveRequest} onClose={() => setMoveRequest(null)} onMoved={() => loadData()} />

      {/* Mark Won Dialog */}
      <Dialog open={!!wonDeal} onOpenChange={(open) => !open && setWonDeal(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={handleWonSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-600">
                <CheckCircle className="size-5" />
                Close Deal as Won
              </DialogTitle>
              <DialogDescription>
                Record final won revenue and close date for <strong>{wonDeal?.name}</strong>.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="won_amount">Final Closed Amount (₹) *</Label>
                <Input
                  id="won_amount"
                  type="number"
                  min="1"
                  value={wonForm.won_amount}
                  onChange={(e) => setWonForm((p) => ({ ...p, won_amount: Number(e.target.value) }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="actual_close_date">Actual Close Date *</Label>
                <Input
                  id="actual_close_date"
                  type="date"
                  value={wonForm.actual_close_date}
                  onChange={(e) => setWonForm((p) => ({ ...p, actual_close_date: e.target.value }))}
                  required
                />
              </div>

              {wonGate && !wonGate.allowed && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3.5 space-y-2.5">
                  <p className="text-xs sm:text-sm font-semibold text-amber-900">Not ready to close as Won:</p>
                  <ul className="list-disc pl-4 text-xs sm:text-sm text-amber-900 space-y-1">
                    {wonGate.missing.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                  {wonGate.can_override ? (
                    <div className="space-y-1.5 pt-1">
                      <Label htmlFor="won_reason" className="text-xs sm:text-sm">Why close it as Won anyway? (saved in the history and audit log)</Label>
                      <Input id="won_reason" value={wonReason} onChange={(e) => setWonReason(e.target.value)} placeholder="e.g. Repeat order, paid upfront" />
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Upload the buyer's purchase order on the deal page first, or ask your Sales Head.</p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setWonDeal(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={actionLoading || (!!wonGate && !wonGate.allowed && (!wonGate.can_override || !wonReason.trim()))}
              >
                {actionLoading ? "Closing..." : "Mark as Won"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mark Lost Dialog */}
      <Dialog open={!!lostDeal} onOpenChange={(open) => !open && setLostDeal(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={handleLostSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <XCircle className="size-5" />
                Close Deal as Lost
              </DialogTitle>
              <DialogDescription>
                Lost deals require a mandatory reason for sales analytics.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="lost_reason" className="text-destructive font-semibold">
                  Lost Reason *
                </Label>
                <select
                  id="lost_reason"
                  className="w-full h-10 sm:h-11 rounded-lg border border-input bg-background px-3 sm:px-3.5 text-sm sm:text-[15px] focus:outline-hidden focus:ring-2 focus:ring-ring"
                  value={lostForm.lost_reason}
                  onChange={(e) => setLostForm((p) => ({ ...p, lost_reason: e.target.value }))}
                  required
                >
                  <option value="">-- Select lost reason --</option>
                  {lostReasons.map((r) => (
                    <option key={r.code} value={r.name}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lost_notes">Additional Lost Notes</Label>
                <Input
                  id="lost_notes"
                  placeholder="Lessons learned or client feedback"
                  value={lostForm.lost_notes}
                  onChange={(e) => setLostForm((p) => ({ ...p, lost_notes: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLostDeal(null)}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={actionLoading}>
                {actionLoading ? "Closing..." : "Confirm Lost"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Deal Dialog */}
      <Dialog open={!!deleteDeal} onOpenChange={(open) => !open && setDeleteDeal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete Deal</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteDeal?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDeal(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSubmit} disabled={actionLoading}>
              {actionLoading ? "Deleting..." : "Delete Deal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
